import React, { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, LoadingSpinner } from '@/shared/components';
import { useMotHistory } from '../hooks/useMotHistory';
import { MotMetricsSummary } from './MotMetricsSummary';
import { MotTestList } from './MotTestList';
import type { MotMetrics } from '../types/motHistory.types';

interface MotHistoryPanelProps {
  vin?: string;
  vrm?: string;
  onMetrics?: (metrics: MotMetrics) => void;
  onLoadingChange?: (isLoading: boolean) => void;
  showHeader?: boolean;
}

export const MotHistoryPanel: React.FC<MotHistoryPanelProps> = ({
  vin,
  vrm,
  onMetrics,
  onLoadingChange,
  showHeader = true,
}) => {
  const { data, isLoading, isError, error } = useMotHistory(vin, vrm);

  useEffect(() => {
    if (data?.metrics && onMetrics) {
      onMetrics(data.metrics);
    }
  }, [data?.metrics, onMetrics]);

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  if (!vin && !vrm) return null;

  const errorStatus =
    error &&
    typeof error === 'object' &&
    'response' in error
      ? (error as { response?: { status?: number } }).response?.status
      : undefined;

  return (
    <Card>
      {showHeader && (
        <CardHeader>
          <CardTitle>MOT History</CardTitle>
        </CardHeader>
      )}
      <CardContent>
        {isLoading && (
          <LoadingSpinner size="sm" message="Loading MOT history…" className="py-4" />
        )}

        {isError && (
          <div className="py-4">
            {errorStatus === 404 ? (
              <p className="text-sm text-mp-muted italic">No MOT history found for this vehicle</p>
            ) : errorStatus === 429 ? (
              <p className="text-sm text-mp-warning">
                MOT history service rate limit reached. Please try again shortly.
              </p>
            ) : (
              <p className="text-sm text-mp-error">
                Unable to load MOT history. Please try again later.
              </p>
            )}
          </div>
        )}

        {data && (
          <div className="space-y-4">
            <div>
              {(data.make || data.model) && (
                <p className="text-xs text-mp-muted mb-3">
                  {[data.make, data.model].filter(Boolean).join(' ')}
                </p>
              )}
              <MotMetricsSummary metrics={data.metrics} />
            </div>
            <div>
              <p className="text-sm font-medium text-mp-foreground mb-2">
                {data.tests.length} test{data.tests.length !== 1 ? 's' : ''} on record
              </p>
              <MotTestList tests={data.tests} />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
